import React, { useEffect } from 'react';
import TranslationHelper from '../../helpers/TranslationHelper';
import {
    AutocompleteColumn,
    ISelectOption,
    RowData,
    SelectColumn,
    TextFieldColumn,
} from './types';
import { Autocomplete, TextField } from '@mui/material';
import { MenuItem } from '@material-ui/core';
import { DIGITS_ONLY_REGEX, DOUBLE_REGEX } from '../../constants/regex';

interface TextFieldProps<T> {
    column: TextFieldColumn;
    row: T;
    handleValueChange: (
        rowId: string | number,
        columnId: string,
        value: any
    ) => void;
}

interface SelectProps<T> {
    column: SelectColumn;
    row: T;
    handleValueChange: (
        rowId: string | number,
        columnId: string,
        value: any
    ) => void;
}

interface AutocompleteProps<T> {
    column: AutocompleteColumn;
    row: T;
    handleValueChange: (
        rowId: string | number,
        columnId: string,
        value: any
    ) => void;
    error?: string;
}
export const TextFieldCell = ({
    handleValueChange,
    column,
    row,
}: TextFieldProps<RowData>) => {
    const value = row[column.id];

    return (
        <TextField
            variant="outlined"
            size="small"
            value={value || ''}
            onChange={(e) =>
                handleValueChange(row.id, column.id, e.target.value)
            }
            placeholder={TranslationHelper.translate(
                column.placeholder || '...'
            )}
            type={column.inputType || 'text'}
            disabled={row.disabled || column.disabled}
            error={column.hasError?.(value)}
            helperText={column.getErrorText?.(value)}
        />
    );
};

export const SelectCell = ({
    handleValueChange,
    column,
    row,
}: SelectProps<RowData>) => {
    const value = row[column.id];
    return (
        <TextField
            id="defaultValue"
            label={TranslationHelper.translate('Default value')}
            name="defaultValue"
            select={true}
            size="small"
            variant="outlined"
            margin="dense"
            value={value}
            disabled={row.disabled || column.disabled}
            onChange={(e) =>
                handleValueChange(row.id, column.id, e.target.value)
            }
        >
            {column.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {TranslationHelper.translate(option.label)}
                </MenuItem>
            ))}
        </TextField>
    );
};

export const AutocompleteCell = ({
    handleValueChange,
    column,
    row,
    error,
}: AutocompleteProps<RowData>) => {
    const rowValue = row[column.id];
    const options =
        column.options?.map((option) => ({
            label: TranslationHelper.translate(String(option.label)),
            value: String(option.value),
        })) || [];

    useEffect(() => {
        if (
            !column.freeSolo &&
            rowValue &&
            !findOption(options, rowValue.value ? rowValue.value : rowValue)
        ) {
            handleValueChange(row.id, column.id, '');
        }
    }, [column.options]);

    const findOption = (options: ISelectOption[] | undefined, value: any) => {
        return options?.find((option) => {
            if (value === '' || value === undefined) {
                return;
            }
            return option.value === String(value);
        })?.label;
    };
    return (
        <Autocomplete
            options={options}
            value={rowValue?.value ? rowValue.value : rowValue || ''}
            noOptionsText={TranslationHelper.translate('No options to display')}
            freeSolo={column.freeSolo}
            size="small"
            inputValue={
                rowValue?.label
                    ? rowValue.label
                    : findOption(options, rowValue) || rowValue || ''
            }
            filterOptions={(options) => options}
            disableClearable
            onChange={(event, newValue) => {
                if (column.freeSolo) {
                    return;
                }
                handleValueChange(row.id, column.id, newValue);
            }}
            onInputChange={(event, newInputValue, reason) => {
                if (!column.freeSolo) {
                    return;
                }

                if (
                    column.valueType === 'integer' &&
                    typeof newInputValue === 'string' &&
                    (newInputValue === '' ||
                        DIGITS_ONLY_REGEX.test(newInputValue))
                ) {
                    handleValueChange(row.id, column.id, newInputValue);
                } else if (
                    column.valueType === 'number' &&
                    typeof newInputValue === 'string' &&
                    DOUBLE_REGEX.test(newInputValue)
                ) {
                    handleValueChange(row.id, column.id, newInputValue);
                } else if (column.valueType === 'string') {
                    handleValueChange(row.id, column.id, newInputValue);
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={TranslationHelper.translate(column.label)}
                    margin="dense"
                    disabled={row.disabled || column.disabled}
                    error={!!error}
                    helperText={error}
                />
            )}
        />
    );
};
